import { EventEmitter, Injectable, Output } from '@angular/core';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Highlighter } from '../../models/table/highlight-resource';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { Payable } from '../../models/entities/payable';
import { map, tap } from 'rxjs/operators';
import { PayableApprover } from '../../models/entities/payableApprover';
import { PayableApproval } from '../../models/entities/payable-approvals';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { PDFSource } from 'ng2-pdf-viewer';
import { UploadedFile } from '../../models/entities/uploaded-file';
import { Payable as PayableInterface } from 'app/components/shared/http/request-interfaces';
import { Transaction } from '../../models/entities/transactions/transaction';
import moment from 'moment-timezone';
import { PayableFilterTab } from './payables-data.service';
import { PayableDatasourceOptions, PayablesResponse } from './payables.datasource';
import { ActivityLog } from '../../models/entities/activity-log';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class PayablesService
{
    @Output() updateWidgets = new EventEmitter<void>();

    private _http: ApplicationHttpClient;
    private _lobMailCheckFee: number = environment.LobMailCheckFeeCents;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * @param {number} organizationId
     * @param {PayableFilterTab} tab
     * @param {string} search
     * @param {string} sortColumn
     * @param {string} sortDirection
     * @param {number} page
     * @param {number} perPage
     * @param {PayableDatasourceOptions} options
     * @param {number} highlight
     * @returns {Observable<PayablesResponse>}
     */
    public getPayables(
            organizationId: number,
            tab: PayableFilterTab = 'unpaid',
            search = '',
            sortColumn: string = 'due_date',
            sortDirection: string = 'desc',
            page: number = 1,
            perPage: number = 25,
            options: PayableDatasourceOptions = {},
            highlight: Highlighter = null,
    ): Observable<PayablesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
            .append('organizationId', organizationId)
            .append('tab', tab)
            .append('search', search)
            .append('sortColumn', sortColumn)
            .append('sortDirection', sortDirection)
            .append('page', page.toString())
            .append('perPage', perPage.toString());

        if (Object.keys(options).length > 0) {
            params = params.append('filters', JSON.stringify(options));
        }

        if (highlight) {
            params = params.append('highlightId', highlight?.id);
            if (highlight?.refocus) {
                params = params.append('pageTo', 'highlighted');
            }
        }

        const url = Routes.MakeLegFiCoreUrl('/payables');
        return this._http.get(url, { params }).pipe(map((resp) => new PayablesResponse(resp)));

    }

    /**
     * @param {number} payableId
     */
    public getPayable(payableId: number): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId);
        return this._http.get(url).pipe(map((res: Object) => {
            return new Payable(res);
        }));
    }

    public previewPayable(
            request: PayableInterface.PreviewPayableRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/preview');
        return this._http.post(url, JSON.stringify(request))
                .pipe(map((res: Object) => {
                    return new Payable(res);
                }));
    }

    public createPayable(request: {
        organizationId: number,
        payable: any,
        markAsPaid?: any,
        payableAction?: any
    }): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables');
        return this._http.post(url, JSON.stringify(request))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public updatePayablePaymentMethodPostApproval(
            payableId: number,
            payableData: PayableInterface.UpdatePayablePaymentMethodPostApprovalRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/payment-method');
        return this._http.put(url, JSON.stringify(payableData))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public updatePayableDetails(
            payableId: number,
            payableData: PayableInterface.UpdatePayableDetailsRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/details');
        return this._http.patch(url, JSON.stringify(payableData))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public attachInvoiceToPayable(
            uploadedFileId: number,
            inboundEmailId: number = null,
            payableId: number = null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/invoice');
        return this._http.post(url, JSON.stringify({
            uploadedFileId: uploadedFileId,
            inboundEmailId: inboundEmailId,
            payableId: payableId,
            organizationId: jwt.orgId,
        }));
    }

    public cancelPayment(payableId: number, cancellationDate?: string): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/cancel');
        const payload = cancellationDate ? { cancellationDate } : {};
        return this._http.post(url, JSON.stringify(payload));
    }

    public deletePayable(payable: Payable): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payable.id);
        return this._http.delete(url).pipe(tap(() => this.updateWidgets.emit()));
    }

    public restorePayables(payableIds: number[]): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/restore');
        return this._http.post(url, JSON.stringify({payables: payableIds})).pipe(tap(() => this.updateWidgets.emit()));
    }

    public getApprovers(): Observable<PayableApprover[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    public updateApprovers(membershipIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers');
        return this._http.post(url, JSON.stringify({
            organizationId: jwt.orgId,
            membershipIds: membershipIds,
        }));
    }

    public getPayableRules(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    public updatePayableApprovalRules(payableRules: object): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules');
        return this._http.post(url, JSON.stringify({
            orgId: jwt.orgId,
            payableRules: payableRules,
        }));
    }

    deletePayableRule(payableRuleId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules/' + payableRuleId);
        return this._http.delete(url);
    }

    setPayableApproval(request: PayableApproval): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url = Routes.MakeLegFiCoreUrl('/payables/approvals');
        if (request.id && request.id > 0) {
            url += '/' + request.id;
            return this._http.put(url, JSON.stringify(request)).pipe(tap(() => this.updateWidgets.emit()));
        } else {
            return this._http.post(url, JSON.stringify(request)).pipe(tap(() => this.updateWidgets.emit()));
        }
    }

    getMappedPayableApproversAndApprovals(payableId: number): Observable<PayableApproval[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers-approvals/' + payableId);
        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((obj) => {
                return new PayableApproval(obj);
            });
        }));
    }

    getWidgetsCount(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/dashboard-widgets');
        return this._http.get(url);
    }

    sendPayableApprovalReminder(payableId: number, payableApproverId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers/' + payableApproverId + '/send-approval-reminder');
        return this._http.post(url, JSON.stringify({
            payableId: payableId,
            organizationId: jwt.orgId,
        }));
    }

    returnToDraft(payableId: number): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/returnToDraft');
        return this._http.post(url, JSON.stringify({}));
    }

    removeAttachment(payableId: number, attachmentId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/attachments/${attachmentId}`);
        return this._http.delete(url);
    }

    /**
     * The file types core answers as JSON rather than as a binary download: `txt` comes back as raw text and
     * the four image types as base64, both for inline display. Everything else is a binary download.
     *
     * Exported and shared because more than one caller has to make the same routing decision — getInvoice()
     * picks the responseType from it, and inbound-email-inbox picks its download path from it. Two private
     * copies drifted apart once already (bmp/webp were previewable here and binary in core), which is
     * exactly the broken-image case the preview's new branch exists to prevent.
     */
    public static readonly JSON_RESPONSE_FILE_TYPES = ['txt', 'jpg', 'jpeg', 'png', 'gif'];

    getInvoice(attachment: UploadedFile): Observable<PDFSource | string | ArrayBuffer> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(`/payables/${attachment.id}/download`);

        // SD-8230: core's downloadAttachment() answers in two different shapes, and the response type has
        // to be chosen to match the shape rather than to match a list of file types.
        //
        //   txt                  -> raw JSON  (the file's text, for inline display)
        //   jpg/jpeg/png/gif     -> base64 JSON  (for inline preview)
        //   everything else      -> a binary download
        //
        // That last branch is what core#4940 added, replacing a throw that made an xls/xlsx (or anything
        // else a user can upload) permanently un-retrievable once stored. Gating arraybuffer to pdf/docx
        // meant every newly-downloadable type arrived on the JSON path, failed JSON.parse and surfaced as
        // an error — so the server fix never reached the user. Asking for JSON only where core actually
        // sends JSON also means the next upload type core permits needs no change here.
        if (attachment?.fileType && PayablesService.JSON_RESPONSE_FILE_TYPES.indexOf(attachment.fileType) !== -1) {
            return this._http.get(url);
        }

        return this._http.get(url, {
            responseType: 'arraybuffer',
        });
    }

    /**
     * Derive a download filename extension from the invoice's Content-Type.
     *
     * SD-8230: this has to happen client-side. core sends a correct Content-Disposition filename, but
     * cors.php exposes only X-LegFiRefresh and X-Trace-Id so the header is unreadable here, and the
     * download fires from a blob: object URL, which carries a MIME type and never a filename.
     *
     * The map mirrors core's FileValidationService::ALLOWED_MIME_TYPES — the same canonical
     * extension-to-mime source upload validation uses, and now the source of the Content-Type core sends
     * — so every type this system accepts saves under its real extension instead of degrading to `.bin`.
     * Exact lookup rather than substring matching: `text/plain` and `text/csv` both contain "text", and
     * the OOXML word and spreadsheet types differ only at the tail.
     */
    private static extensionForContentType(contentType: string): string {
        const extensionsByMimeType: { [mimeType: string]: string } = {
            'application/pdf': 'pdf',
            'image/jpeg': 'jpg',
            'image/png': 'png',
            'image/gif': 'gif',
            'image/bmp': 'bmp',
            'image/tiff': 'tiff',
            'image/webp': 'webp',
            'image/heic': 'heic',
            'image/heif': 'heif',
            'application/msword': 'doc',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/vnd.ms-excel': 'xls',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
            'text/plain': 'txt',
            'text/csv': 'csv',
            'application/csv': 'csv',
            'application/rtf': 'rtf',
            'text/rtf': 'rtf',
            'video/mp4': 'mp4',
            'video/quicktime': 'mov',
            'video/x-m4v': 'm4v',
        };

        // A Content-Type may carry parameters, e.g. `text/plain; charset=utf-8`.
        const mimeType = contentType.split(';')[0].trim().toLowerCase();
        const mapped = extensionsByMimeType[mimeType];

        if (mapped) {
            return mapped;
        }

        // Exact matching is right for the mapped types, but it regressed the long tail of text/*: under the
        // old substring check anything containing "text" saved as .txt, and `text/html` and friends would
        // now degrade to .bin. Any text/* is readable as .txt, which beats a file the OS cannot open.
        if (mimeType.indexOf('text/') === 0) {
            return 'txt';
        }

        return 'bin';
    }

    public downloadPayableInvoice(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const downloadUrl = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/invoice-download`);

        return this._http.get(downloadUrl, {
            responseType: 'blob'
        }).pipe(
                map((response: Blob) => {
                    const contentType = response.type || 'application/octet-stream';

                    const extension = PayablesService.extensionForContentType(contentType);
                    const filename = `invoice-${payableId}.${extension}`;

                    const blobUrl = window.URL.createObjectURL(response);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up
                    window.URL.revokeObjectURL(blobUrl);
                    return true;
                })
        );
    }

    getPotentialTransactionMatches(payable: Payable): Observable<Transaction[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/potential-transaction-matches`);
        return this._http.post(url, JSON.stringify({
            organizationId: LegFiJwtService.read().orgId,
            payable: payable,
        }));
    }

    getPayableTrackingData(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/tracking`);
        return this._http.get(url);
    }

    minScheduledPaymentDate(): Date {
        const userTZ = moment.tz.guess();
        const userDateTime = moment().tz(userTZ);
        const estDateTime = userDateTime.clone().tz('America/New_York');
        const eleven55EST = moment.tz('America/New_York').set({hour: 11, minute: 55, second: 0, millisecond: 0});

        if (estDateTime.isBefore(eleven55EST)) {
            return new Date();
        } else {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow;
        }
    }

    downloadPrintableCheckFile(payable: Payable): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payable.id}/download-printable-check`);

        return this._http.get(url, {
            headers: newHeaders,
            responseType: 'arraybuffer',
        });
    }

    downloadCheckImage(payableId: number): Observable<Blob> {
        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/check-image`);
        return this._http.get(url, { responseType: 'blob' });
    }

    getFormattedLobMailCheckFee(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(this._lobMailCheckFee / 100);
    }

    getCheckPrintingSettings(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/check-printing?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    updateCheckPrintingSettings(settings: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/check-printing');
        return this._http.put(url, JSON.stringify({
            ...settings,
            organizationId: jwt.orgId,
        }));
    }

    getPayableFilters(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(`/payables/payableFilters`);

        return this._http.get(url);
    }

    getActivityLogs(
            filters?: {
                payableId?: number;
                eventTypes?: string[];
                dateFrom?: string;
                dateTo?: string;
            },
            page: number = 1,
            perPage: number = 25
    ): Observable<any> {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
                .append('organizationId', jwt.orgId.toString())
                .append('page', page.toString())
                .append('perPage', perPage.toString());

        if (filters?.payableId) {
            params = params.append('payableId', filters.payableId.toString());
        }
        if (filters?.eventTypes?.length) {
            filters.eventTypes.forEach(type => {
                params = params.append('eventTypes[]', type);
            });
        }
        if (filters?.dateFrom) {
            params = params.append('dateFrom', filters.dateFrom);
        }
        if (filters?.dateTo) {
            params = params.append('dateTo', filters.dateTo);
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/activity-logs');
        return this._http.get(url, { params });
    }

    /**
     * Clear all fulfillment errors for a payable
     */
    public clearFulfillmentErrors(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/clear-fulfillment-errors`);
        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Super admin action to manually mark a mailed check as processed.
     * Used when USPS fails to scan delivery events.
     */
    public markAsProcessed(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/mark-as-processed`);
        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Export payable attachments as ZIP file
     */
    exportPayableAttachments(payableId: number): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/export-attachments`);
        return this._http.post(url, null, { responseType: 'blob' });
    }

    /**
     * Export payables to CSV format
     * @param organizationId Organization ID
     * @param tab Current tab filter
     * @param search Search string
     * @param options Filter options
     * @returns Observable of Blob
     */
    public exportPayablesCsv(
        organizationId: number,
        tab: PayableFilterTab = 'unpaid',
        search: string = '',
        options: PayableDatasourceOptions = {}
    ): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
            .append('organizationId', organizationId)
            .append('tab', tab)
            .append('search', search);

        // Add filter options to params
        if (Object.keys(options).length > 0) {
            params = params.append('filters', JSON.stringify(options));
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/export');
        return this._http.get(url, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Bulk export payables (invoices, attachments, or CSV)
     */
    public bulkExport(payableIds: number[], type: 'invoices' | 'attachments' | 'csv'): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/bulk/export');
        return this._http.post(url, JSON.stringify({ payableIds, type }), {
            responseType: 'blob'
        });
    }
}
